/** Snackbar variant types */
export type SnackbarVariant = 'fill' | 'soft';

/** Snackbar context types (semantic color schemes) */
export type SnackbarContext = 'brand' | 'neutral' | 'positive' | 'negative' | 'caution' | 'info';

/** Display strategy for multiple snackbars */
export type SnackbarDisplayStrategy = 'sequential' | 'stack';

/** ARIA live region politeness levels */
export type SnackbarAriaLive = 'polite' | 'assertive';

/**
 * Configuration interface for creating a snackbar.
 *
 * @example
 *   ```typescript
 *   const config: HxSnackbarConfig = {
 *     message: 'Document saved successfully',
 *     variant: 'fill',
 *     context: 'positive',
 *     hasIcon: true,
 *     hasClose: true,
 *     duration: 4000,
 *     position: 'top-right',
 *   };
 *   ```;
 */

/** Snackbar position types (with RTL auto-mirroring for horizontal positions) */
export type SnackbarPosition =
  | 'top-right'
  | 'top-left'
  | 'bottom-right'
  | 'bottom-left'
  | 'top-center'
  | 'bottom-center';

export type HxSnackbarConfig = {
  /**
   * Optional: Maximum number of visible snackbars in stack mode. Only applies when displayStrategy is 'stack'.
   *
   * @default 3
   */
  maxVisible?: number;
  /**
   * Optional: Position of the snackbar container on the screen.
   *
   * @default 'bottom-right' (mirrored in RTL)
   */
  position?: SnackbarPosition;
  /** Optional: Notification message text. When omitted or blank, the snackbar displays and announces "Notification". */
  message: string;

  /**
   * Optional: Visual variant.
   *
   * - 'fill': Solid background with high contrast (default)
   * - 'soft': Lighter background with reduced contrast
   *
   * @default 'fill'
   */
  variant?: SnackbarVariant;

  /**
   * Optional: Semantic context determines color scheme.
   *
   * - 'brand': Primary brand color (default)
   * - 'neutral': Standard gray tones
   * - 'positive': Success/confirmation (green)
   * - 'negative': Error/destructive (red)
   * - 'caution': Warning/attention (yellow/orange)
   * - 'info': Informational (blue)
   *
   * @default 'brand'
   */
  context?: SnackbarContext;

  /**
   * Optional: Auto-dismiss duration in milliseconds.
   *
   * - > 0: Auto-dismiss after specified duration
   * - 0: No auto-dismiss (manual dismissal only)
   *
   * @default 4000 (4 seconds)
   */
  duration?: number;

  /**
   * Optional: Show context-specific semantic icon. Icon is automatically selected based on context:
   *
   * - Positive: checkmark (cc-icon-check)
   * - Negative: X (cc-icon-x)
   * - Caution: warning triangle (cc-icon-alert-triangle)
   * - Info: information circle (cc-icon-info)
   * - Neutral: bell (cc-icon-bell)
   * - Brand: bell (cc-icon-bell)
   *
   * @default false
   */
  hasIcon?: boolean;

  /**
   * Optional: Show action button. If true, actionLabel must be provided.
   *
   * @default false
   */
  hasAction?: boolean;

  /**
   * Optional: Action button label text. Required if hasAction is true.
   *
   * @example
   *   ('Undo', 'Retry', 'View');
   */
  actionLabel?: string;

  /**
   * Optional: Action button click handler. Snackbar dismisses immediately when action is clicked. Handler executes
   * asynchronously after dismissal begins.
   *
   * @example
   *   ```typescript
   *   onAction: () => {
   *     console.log('Action clicked');
   *     // Async operation
   *   }
   *   ```;
   */
  onAction?: () => void | Promise<void>;

  /**
   * Optional: Show close button (X icon). Allows manual dismissal before auto-dismiss.
   *
   * @default false
   */
  hasClose?: boolean;

  /**
   * Optional: Display strategy for multiple snackbars.
   *
   * - 'sequential': Queue snackbars, show one at a time (FIFO)
   * - 'stack': Stack snackbars vertically (max 3 visible)
   *
   * @default 'sequential'
   */
  displayStrategy?: SnackbarDisplayStrategy;

  /**
   * Optional: Additional CSS class for custom styling. Applied to root snackbar element.
   *
   * @example
   *   'custom-snackbar-style';
   */
  customClass?: string;

  /**
   * Optional: ARIA live region politeness level.
   *
   * - 'polite': Wait for pause in screen reader speech
   * - 'assertive': Interrupt screen reader immediately
   *
   * Auto-set to 'assertive' for negative/caution contexts if not specified.
   *
   * @default 'polite' (or 'assertive' for negative/caution)
   */
  ariaLive?: SnackbarAriaLive;

  /**
   * Optional: Custom announcement message for screen readers. If not provided, message content is announced.
   *
   * @example
   *   'Error: Failed to save document. Please try again.';
   */
  announcementMessage?: string;
};

/** Type guard to check if config has required action properties */
export function hasValidAction(config: HxSnackbarConfig): boolean {
  return config.hasAction === true && typeof config.actionLabel === 'string' && config.actionLabel.trim().length > 0;
}

/** Type guard to validate config */
export function isValidSnackbarConfig(config: Partial<HxSnackbarConfig>): config is HxSnackbarConfig {
  if (!config || typeof config !== 'object') {
    return false;
  }

  if (typeof config.message === 'string') {
    return config.message.trim().length > 0;
  }

  // Message omitted - still valid because fallback text is used
  return true;
}
